import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';

import BasePage from '../../components/BasePage';
import DataTable, { Column, FilterField } from '../../components/DataTable';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';

import AddPartnerModal from './AddPartnerModal';
import EditPartnerModal from './EditPartnerModal';
import DeletePartnerModal from './DeletePartnerModal';

import { IconButton, CreateButton, CategoryChip } from './styles';

interface IPartner {
  id: string;
  name: string;
  description: string;
  address: string;
  address2: string;
  postal_code: string;
  city: {
    nome: string;
    uf: string;
  };
  state: string;
  whatsapp: string;
  phone: string;
  email: string;
  avatar: string;
  category: {
    id: string;
    title: string;
  };
  syndicate: {
    id: string;
    nome_fantasia: string;
  };
}

interface ICategory {
  id: string;
  title: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

const UnionsPartners: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [unions, setUnions] = useState<ISyndicate[]>([]);

  const [partnerId, setPartnerId] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    document.title = 'Convênios - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get<IPartner[]>('/unions-partners', {
          params: { ...filterParams, page: currentPage },
        });
        if (cancelled) return;
        setPartners(response.data);
        setTotal(Number(response.headers['x-total-partners'] || 0));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filterParams, currentPage]);

  useEffect(() => {
    const load = async () => {
      const categoriesRes = await api.get<ICategory[]>(
        '/unions-partners/categories',
      );
      setCategories(categoriesRes.data);
      if (user.role === 'admin') {
        const unionsRes = await api.get<ISyndicate[]>('/unions');
        setUnions(unionsRes.data);
      }
    };
    load();
  }, [user.role]);

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'name',
        label: 'Nome',
        type: 'text',
        placeholder: 'Digite o nome',
        span: 5,
      },
      {
        key: 'category_id',
        label: 'Categoria',
        type: 'select',
        placeholder: 'Todas',
        options: categories.map(c => ({ value: c.id, label: c.title })),
        span: user.role === 'admin' ? 3 : 7,
      },
    ];

    if (user.role === 'admin') {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 4,
      });
    }

    return base;
  }, [categories, unions, user.role]);

  const columns = useMemo<Column<IPartner>[]>(
    () => [
      {
        key: 'name',
        label: 'Nome',
        width: '22%',
        render: p => (
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</span>
        ),
      },
      {
        key: 'description',
        label: 'Descrição',
        render: p => (
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: '#475569',
            }}
            title={p.description}
          >
            {p.description}
          </span>
        ),
      },
      {
        key: 'category',
        label: 'Categoria',
        width: '14%',
        render: p =>
          p.category?.title ? (
            <CategoryChip>{p.category.title}</CategoryChip>
          ) : (
            '—'
          ),
      },
      {
        key: 'address',
        label: 'Endereço',
        width: '28%',
        render: p =>
          [
            p.address,
            p.address2,
            p.city ? `${p.city.nome}/${p.city.uf}` : undefined,
            p.postal_code,
          ]
            .filter(Boolean)
            .join(' · ') || '—',
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  return (
    <BasePage title="Convênios">
      <Container fluid style={{ padding: 0 }}>
        <DataTable<IPartner>
          title="Convênios"
          subtitle="Gerencie parceiros disponíveis para os associados"
          columns={columns}
          data={partners}
          rowKey="id"
          loading={loading}
          page={currentPage}
          total={total}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <CreateButton type="button" onClick={() => setShowAddModal(true)}>
              <FiPlus size={14} />
              Novo convênio
            </CreateButton>
          )}
          rowActions={row => (
            <>
              <IconButton
                type="button"
                title="Editar"
                onClick={() => {
                  setPartnerId(row.id);
                  setShowEditModal(true);
                }}
              >
                <FiEdit size={14} />
              </IconButton>
              <IconButton
                type="button"
                title="Remover"
                $danger
                onClick={() => {
                  setPartnerId(row.id);
                  setShowDeleteModal(true);
                }}
              >
                <FiTrash2 size={14} />
              </IconButton>
            </>
          )}
          emptyMessage="Nenhum convênio encontrado."
        />

        {showAddModal && (
          <AddPartnerModal
            handleShowModal={setShowAddModal}
            showModal={showAddModal}
            handlePartners={setPartners}
            handleLoading={setLoading}
          />
        )}

        {showEditModal && (
          <EditPartnerModal
            handleShowModal={setShowEditModal}
            partnerId={partnerId}
            showModal={showEditModal}
            handlePartners={setPartners}
            handleLoading={setLoading}
          />
        )}

        {showDeleteModal && (
          <DeletePartnerModal
            handleShowModal={setShowDeleteModal}
            partnerId={partnerId}
            showModal={showDeleteModal}
            handlePartners={setPartners}
            handleLoading={setLoading}
          />
        )}
      </Container>
    </BasePage>
  );
};

export default UnionsPartners;
